import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FlaskConical, Plus, Search, Play, Pause, CheckCircle, BarChart3,
  TrendingUp, Users, MoreVertical, Trash2, Eye, Trophy, QrCode, Link as LinkIcon,
  ArrowRight, RefreshCw, Calendar, X, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { apiV1 } from '@/api/client';

const statusColors = {
  draft: 'bg-slate-500',
  running: 'bg-blue-500',
  paused: 'bg-amber-500',
  completed: 'bg-emerald-500',
  archived: 'bg-gray-500',
};

const statusLabels = {
  draft: 'Draft',
  running: 'Running',
  paused: 'Paused',
  completed: 'Completed',
  archived: 'Archived',
};

export default function Experiments() {
  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Create dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [itemType, setItemType] = useState('qr'); // 'qr' or 'url'
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [newExperiment, setNewExperiment] = useState({
    name: '',
    description: '',
    hypothesis: '',
  });
  
  // Comparison view state
  const [showComparisonDialog, setShowComparisonDialog] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);

  useEffect(() => {
    loadExperiments();
  }, []);

  const loadExperiments = async () => {
    setLoading(true);
    try {
      const response = await apiV1.get('/experiments/');
      if (response.data.success) {
        setExperiments(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load experiments:', error);
      setExperiments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableItems = async (type) => {
    setLoadingItems(true);
    try {
      if (type === 'qr') {
        const response = await apiV1.get('/qrcodes/', { params: { per_page: 200 } });
        if (response.data.success) {
          setAvailableItems(response.data.data.map(qr => ({
            id: qr.id,
            name: qr.name,
            type: 'qr',
            scans: qr.scans || 0,
            status: qr.status,
            created_at: qr.created_at,
          })));
        }
      } else {
        const response = await apiV1.get('/urls/', { params: { per_page: 200 } });
        if (response.data.success) {
          setAvailableItems(response.data.data.map(url => ({
            id: url.id,
            name: url.title || url.slug,
            type: 'url',
            scans: url.total_clicks || 0,
            status: url.status,
            created_at: url.created_at,
          })));
        }
      }
    } catch (error) {
      toast.error('Failed to load items');
      setAvailableItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleItemTypeChange = (type) => {
    setItemType(type);
    setSelectedItems([]);
    loadAvailableItems(type);
  };

  const toggleItemSelection = (item) => {
    if (selectedItems.find(i => i.id === item.id)) {
      setSelectedItems(selectedItems.filter(i => i.id !== item.id));
    } else if (selectedItems.length < 5) {
      setSelectedItems([...selectedItems, item]);
    } else {
      toast.warning('Maximum 5 items can be compared');
    }
  };

  const handleCreateExperiment = async () => {
    if (!newExperiment.name.trim()) {
      toast.error('Experiment name is required');
      return;
    }
    if (selectedItems.length < 2) {
      toast.error('Select at least 2 items to compare');
      return;
    }

    try {
      const response = await apiV1.post('/experiments/', {
        name: newExperiment.name,
        description: newExperiment.description,
        hypothesis: newExperiment.hypothesis,
        item_type: itemType,
        variants: selectedItems.map((item, idx) => ({
          name: `Variant ${String.fromCharCode(65 + idx)}`, // A, B, C, etc.
          item_id: item.id,
          item_name: item.name,
          traffic_percentage: Math.floor(100 / selectedItems.length),
        })),
      });
      
      if (response.data.success) {
        toast.success('A/B Test created!');
        resetCreateDialog();
        loadExperiments();
      } else {
        toast.error(response.data.detail || 'Failed to create experiment');
      }
    } catch (error) {
      toast.error('Failed to create experiment');
    }
  };

  const resetCreateDialog = () => {
    setShowCreateDialog(false);
    setCreateStep(1);
    setItemType('qr');
    setSelectedItems([]);
    setAvailableItems([]);
    setNewExperiment({ name: '', description: '', hypothesis: '' });
  };

  const loadComparisonData = async (experiment) => {
    setLoadingComparison(true);
    setShowComparisonDialog(experiment);
    
    try {
      // Fetch analytics for each variant
      const variantData = await Promise.all(
        (experiment.variants || []).map(async (variant) => {
          try {
            let analytics = { total: 0, unique: 0 };
            if (experiment.item_type === 'qr') {
              const resp = await apiV1.get(`/qrcodes/${variant.item_id}/analytics`);
              if (resp.data.success) {
                analytics = {
                  total: resp.data.data.total_scans || 0,
                  unique: resp.data.data.unique_scans || 0,
                };
              }
            } else {
              const resp = await apiV1.get(`/urls/${variant.item_id}/analytics/summary`);
              if (resp.data.success) {
                analytics = {
                  total: resp.data.data.total_clicks || 0,
                  unique: resp.data.data.unique_clicks || 0,
                };
              }
            }
            return { ...variant, analytics };
          } catch {
            return { ...variant, analytics: { total: 0, unique: 0 } };
          }
        })
      );
      
      // Calculate winner
      const maxScans = Math.max(...variantData.map(v => v.analytics.total));
      const winner = variantData.find(v => v.analytics.total === maxScans && maxScans > 0);
      
      setComparisonData({
        ...experiment,
        variants: variantData,
        winner: winner?.name,
        totalInteractions: variantData.reduce((sum, v) => sum + v.analytics.total, 0),
      });
    } catch (error) {
      toast.error('Failed to load comparison data');
    } finally {
      setLoadingComparison(false);
    }
  };

  const handleStart = async (id) => {
    try {
      await apiV1.post(`/experiments/${id}/start`);
      toast.success('Experiment started!');
      loadExperiments();
    } catch (error) {
      toast.error('Failed to start experiment');
    }
  };

  const handlePause = async (id) => {
    try {
      await apiV1.post(`/experiments/${id}/pause`);
      toast.success('Experiment paused');
      loadExperiments();
    } catch (error) {
      toast.error('Failed to pause experiment');
    }
  };

  const handleComplete = async (id) => {
    try {
      await apiV1.post(`/experiments/${id}/complete`);
      toast.success('Experiment marked as complete');
      loadExperiments();
    } catch (error) {
      toast.error('Failed to complete experiment');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this experiment?')) return;
    try {
      await apiV1.delete(`/experiments/${id}`);
      toast.success('Experiment deleted');
      loadExperiments();
    } catch (error) {
      toast.error('Failed to delete experiment');
    }
  };

  const filteredExperiments = experiments.filter(exp =>
    exp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    exp.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="experiments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">A/B Testing</h1>
          <p className="text-muted-foreground mt-1">Compare performance across QR codes or short URLs</p>
        </div>
        <Button onClick={() => { setShowCreateDialog(true); setCreateStep(1); }} data-testid="create-experiment-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Comparison
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search experiments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="experiment-search"
        />
      </div>

      {/* Experiments List */}
      {loading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2 mt-2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredExperiments.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <FlaskConical className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No experiments yet</h3>
            <p className="text-muted-foreground mb-4">Create your first A/B test to compare QR codes or URLs</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Comparison
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredExperiments.map((experiment) => (
            <Card key={experiment.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {experiment.item_type === 'qr' ? (
                        <QrCode className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <LinkIcon className="w-4 h-4 text-muted-foreground" />
                      )}
                      <CardTitle className="text-lg">{experiment.name}</CardTitle>
                    </div>
                    <CardDescription className="mt-1 line-clamp-2">
                      {experiment.description || 'No description'}
                    </CardDescription>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => loadComparisonData(experiment)}>
                        <BarChart3 className="w-4 h-4 mr-2" />
                        View Comparison
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {experiment.status === 'draft' && (
                        <DropdownMenuItem onClick={() => handleStart(experiment.id)}>
                          <Play className="w-4 h-4 mr-2" />
                          Start Test
                        </DropdownMenuItem>
                      )}
                      {experiment.status === 'running' && (
                        <>
                          <DropdownMenuItem onClick={() => handlePause(experiment.id)}>
                            <Pause className="w-4 h-4 mr-2" />
                            Pause
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleComplete(experiment.id)}>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Mark Complete
                          </DropdownMenuItem>
                        </>
                      )}
                      {experiment.status === 'paused' && (
                        <DropdownMenuItem onClick={() => handleStart(experiment.id)}>
                          <Play className="w-4 h-4 mr-2" />
                          Resume
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleDelete(experiment.id)} className="text-destructive">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <Badge className={`${statusColors[experiment.status]} text-white w-fit mt-2`}>
                  {statusLabels[experiment.status]}
                </Badge>
              </CardHeader>
              <CardContent>
                {/* Variants Preview */}
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground font-medium">
                    Comparing {experiment.variants?.length || 0} {experiment.item_type === 'qr' ? 'QR Codes' : 'URLs'}
                  </p>
                  <div className="space-y-1">
                    {(experiment.variants || []).slice(0, 3).map((variant, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">
                          {String.fromCharCode(65 + idx)}
                        </span>
                        <span className="truncate text-muted-foreground">{variant.item_name || variant.name}</span>
                      </div>
                    ))}
                    {(experiment.variants?.length || 0) > 3 && (
                      <p className="text-xs text-muted-foreground pl-7">+{experiment.variants.length - 3} more</p>
                    )}
                  </div>
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-4"
                  onClick={() => loadComparisonData(experiment)}
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  View Results
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog - Step 1: Select Type */}
      <Dialog open={showCreateDialog && createStep === 1} onOpenChange={(open) => !open && resetCreateDialog()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create A/B Comparison</DialogTitle>
            <DialogDescription>Step 1: Select what you want to compare</DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <div className="grid grid-cols-2 gap-4">
              <Card 
                className={`cursor-pointer transition-all hover:shadow-md ${itemType === 'qr' ? 'ring-2 ring-primary' : ''}`}
                onClick={() => handleItemTypeChange('qr')}
              >
                <CardContent className="py-6 text-center">
                  <QrCode className="w-12 h-12 mx-auto mb-3 text-primary" />
                  <h3 className="font-medium">QR Codes</h3>
                  <p className="text-sm text-muted-foreground mt-1">Compare scan performance</p>
                </CardContent>
              </Card>
              <Card 
                className={`cursor-pointer transition-all hover:shadow-md ${itemType === 'url' ? 'ring-2 ring-primary' : ''}`}
                onClick={() => handleItemTypeChange('url')}
              >
                <CardContent className="py-6 text-center">
                  <LinkIcon className="w-12 h-12 mx-auto mb-3 text-primary" />
                  <h3 className="font-medium">Short URLs</h3>
                  <p className="text-sm text-muted-foreground mt-1">Compare click performance</p>
                </CardContent>
              </Card>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetCreateDialog}>Cancel</Button>
            <Button onClick={() => { setCreateStep(2); loadAvailableItems(itemType); }}>
              Next: Select Items
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog - Step 2: Select Items */}
      <Dialog open={showCreateDialog && createStep === 2} onOpenChange={(open) => !open && resetCreateDialog()}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Select {itemType === 'qr' ? 'QR Codes' : 'Short URLs'} to Compare</DialogTitle>
            <DialogDescription>Step 2: Choose 2-5 items to compare (selected: {selectedItems.length})</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {loadingItems ? (
              <div className="space-y-2">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : availableItems.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No {itemType === 'qr' ? 'QR codes' : 'short URLs'} found</p>
                <Button variant="link" asChild className="mt-2">
                  <Link to={itemType === 'qr' ? '/qrs/new' : '/urls/new'}>
                    Create one first
                  </Link>
                </Button>
              </div>
            ) : (
              <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-2">
                  {availableItems.map((item) => {
                    const isSelected = selectedItems.find(i => i.id === item.id);
                    return (
                      <div
                        key={item.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected ? 'bg-primary/5 border-primary' : 'hover:bg-muted/50'
                        }`}
                        onClick={() => toggleItemSelection(item)}
                      >
                        <Checkbox checked={!!isSelected} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{item.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.scans} {itemType === 'qr' ? 'scans' : 'clicks'} • {item.status}
                          </p>
                        </div>
                        {isSelected && (
                          <Badge variant="secondary">
                            {String.fromCharCode(65 + selectedItems.findIndex(i => i.id === item.id))}
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
            
            {/* Selected Preview */}
            {selectedItems.length > 0 && (
              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium mb-2">Selected for comparison:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedItems.map((item, idx) => (
                    <Badge key={item.id} variant="outline" className="gap-1">
                      <span className="font-bold">{String.fromCharCode(65 + idx)}:</span> {item.name}
                      <X 
                        className="w-3 h-3 ml-1 cursor-pointer hover:text-destructive" 
                        onClick={(e) => { e.stopPropagation(); toggleItemSelection(item); }}
                      />
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateStep(1)}>Back</Button>
            <Button onClick={() => setCreateStep(3)} disabled={selectedItems.length < 2}>
              Next: Name Test
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog - Step 3: Name & Create */}
      <Dialog open={showCreateDialog && createStep === 3} onOpenChange={(open) => !open && resetCreateDialog()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Name Your A/B Test</DialogTitle>
            <DialogDescription>Step 3: Add details and create the test</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Test Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Homepage QR vs Product QR"
                value={newExperiment.name}
                onChange={(e) => setNewExperiment({ ...newExperiment, name: e.target.value })}
                data-testid="experiment-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="What are you trying to learn from this comparison?"
                value={newExperiment.description}
                onChange={(e) => setNewExperiment({ ...newExperiment, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hypothesis">Hypothesis (optional)</Label>
              <Textarea
                id="hypothesis"
                placeholder="We believe that X will outperform Y because..."
                value={newExperiment.hypothesis}
                onChange={(e) => setNewExperiment({ ...newExperiment, hypothesis: e.target.value })}
                rows={2}
              />
            </div>
            
            {/* Summary */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium mb-2">Comparison Summary</p>
              <p className="text-sm text-muted-foreground">
                Comparing {selectedItems.length} {itemType === 'qr' ? 'QR codes' : 'short URLs'}:
              </p>
              <div className="flex flex-wrap gap-1 mt-1">
                {selectedItems.map((item, idx) => (
                  <Badge key={item.id} variant="outline" className="text-xs">
                    {String.fromCharCode(65 + idx)}: {item.name}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateStep(2)}>Back</Button>
            <Button onClick={handleCreateExperiment} disabled={!newExperiment.name.trim()}>
              <Check className="w-4 h-4 mr-2" />
              Create Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comparison Results Dialog */}
      <Dialog open={!!showComparisonDialog} onOpenChange={() => { setShowComparisonDialog(null); setComparisonData(null); }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {showComparisonDialog?.item_type === 'qr' ? <QrCode className="w-5 h-5" /> : <LinkIcon className="w-5 h-5" />}
              {showComparisonDialog?.name}
            </DialogTitle>
            <DialogDescription>{showComparisonDialog?.description || 'Performance comparison'}</DialogDescription>
          </DialogHeader>
          
          {loadingComparison ? (
            <div className="py-8 space-y-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : comparisonData ? (
            <div className="py-4 space-y-6">
              {/* Winner Banner */}
              {comparisonData.winner && comparisonData.totalInteractions > 0 && (
                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-center">
                  <Trophy className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
                  <p className="font-medium text-emerald-700 dark:text-emerald-400">
                    Leading: {comparisonData.winner}
                  </p>
                </div>
              )}
              
              {/* Variants Comparison */}
              <div className="space-y-3">
                {comparisonData.variants?.map((variant, idx) => {
                  const isWinner = variant.name === comparisonData.winner;
                  const maxTotal = Math.max(...comparisonData.variants.map(v => v.analytics.total));
                  const percentage = maxTotal > 0 ? (variant.analytics.total / maxTotal) * 100 : 0;
                  
                  return (
                    <div key={idx} className={`p-4 rounded-lg border ${isWinner ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/10' : ''}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${isWinner ? 'bg-emerald-500 text-white' : 'bg-muted'}`}>
                            {String.fromCharCode(65 + idx)}
                          </span>
                          <span className="font-medium">{variant.item_name || variant.name}</span>
                          {isWinner && <Trophy className="w-4 h-4 text-emerald-500" />}
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg">{variant.analytics.total.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">
                            {showComparisonDialog?.item_type === 'qr' ? 'scans' : 'clicks'}
                          </p>
                        </div>
                      </div>
                      <Progress value={percentage} className={`h-2 ${isWinner ? '[&>div]:bg-emerald-500' : ''}`} />
                      <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                        <span>{variant.analytics.unique.toLocaleString()} unique</span>
                        <span>{variant.analytics.total > 0 ? Math.round((variant.analytics.unique / variant.analytics.total) * 100) : 0}% unique rate</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* No Data Message */}
              {comparisonData.totalInteractions === 0 && (
                <div className="text-center py-6 text-muted-foreground">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No interactions recorded yet</p>
                  <p className="text-sm">Data will appear once your {showComparisonDialog?.item_type === 'qr' ? 'QR codes are scanned' : 'URLs are clicked'}</p>
                </div>
              )}
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowComparisonDialog(null); setComparisonData(null); }}>
              Close
            </Button>
            <Button variant="outline" onClick={() => loadComparisonData(showComparisonDialog)}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
